import { Slider } from "../../ui/slider";
import { IoCloseSharp } from "react-icons/io5";
import { FaWallet } from "react-icons/fa";
import { useState, useEffect } from "react";
import styles from './A23BuyIn.module.css';
import { 
  useOpenBuyInModal,
  useOpenRebuyInModal,
  useGameDefinition,
  useBuyInTimeInSeconds,
  useAntiBanking
} from "@/src/hooks/data/useGameTable";
import useGameSocket from "@/src/hooks/utils/useGameSocket";
import { usePlayerBalance } from "@/src/hooks/data/useLobby";
import { RUPEE_SYMBOL } from "@/Poker/data/Constants";
import { Drawer, DrawerClose, DrawerContent } from "../../ui/drawer";
import { closeRebuyIn } from "@/Poker/redux/slices/gameTableSlice";
import { useDispatch } from "react-redux";
import useGameConnection from "@/src/hooks/data/useGameConnection";
import { showToast } from "@/Poker/utils/toastUtils";
import { TOAST_INFO } from "@/Poker/data/Constants";

export default function A23BuyIn({ tempTableId }) {
    const dispatch = useDispatch();
    const { connected } = useGameConnection(tempTableId);
    const openBuyInModal = useOpenBuyInModal(tempTableId);
    const openRebuyInModal = useOpenRebuyInModal(tempTableId);
    const gameDefinition = useGameDefinition(tempTableId);
    const { smallBlind, bigBlind, maxbuyInAmount, minBuyInAmount, antiBankingTime } = gameDefinition || {};
    const buyInTimeInSeconds = useBuyInTimeInSeconds(tempTableId);
    const antiBanking = useAntiBanking(tempTableId);
    const { totalBalance } = usePlayerBalance();
    const [buyInLoader, setBuyInLoader] = useState(false);
    // --- Anti-banking logic ---
    let antiBankActive = false;
    let antiBankAmount = 0;
    if (antiBanking && antiBanking.isAntiBank) {
        antiBankActive = true;
        antiBankAmount = Number(antiBanking.antiBankAmount) || 0;
    }
    // Determine slider min/max and disabled state
    let sliderMin = minBuyInAmount;
    let sliderMax = maxbuyInAmount;
    let sliderDisabled = false;
    let showAntiBankNote = false;
    let noteAmount = null;
    if (antiBankActive) {
        if (antiBankAmount > maxbuyInAmount) {
            sliderMin = antiBankAmount;
            sliderMax = antiBankAmount;
            sliderDisabled = true;
            showAntiBankNote = true;
            noteAmount = antiBankAmount;
        } else if (antiBankAmount > minBuyInAmount) {
            sliderMin = antiBankAmount;
            showAntiBankNote = true;
            noteAmount = antiBankAmount;
        }
    }
    // --- End anti-banking logic ---

    // Ensure sliderMax does not exceed user's total balance
    if (totalBalance < sliderMax) {
        sliderMax = totalBalance;
    }

    const [entryAmount, setEntryAmount] = useState(sliderMin);
    const { sendDReserveProtocol, sendDRebuyInProtocol, sendJoinProtocol, sendRebuyInProtocol } = useGameSocket(tempTableId);
    const [autoRebuyValue, setAutoRebuyValue] = useState(false);
    const [timeLeft, setTimeLeft] = useState(null);

    useEffect(() => {
        setEntryAmount(sliderMin);
    }, [sliderMin]);

    // Timer effect
    useEffect(() => {
        if (!buyInTimeInSeconds || buyInTimeInSeconds <= 0 || (!openBuyInModal && !openRebuyInModal)) return;

        setTimeLeft(buyInTimeInSeconds);

        const timer = setInterval(() => {
            setTimeLeft((prevTime) => {
                if (prevTime <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [buyInTimeInSeconds, openBuyInModal, openRebuyInModal]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleOutsideClick = () => {
        if (openBuyInModal) {
            sendDReserveProtocol();
        }

        if (openRebuyInModal) {
            sendDRebuyInProtocol(autoRebuyValue);
        }
    }

    const handleCloseMark = () => {
        if (openBuyInModal) {
            sendDReserveProtocol();
        }

        if (openRebuyInModal) {
            dispatch(closeRebuyIn({ tempTableId }));
            sendDRebuyInProtocol();
        }
    }

    const handleBuyInButton = () => {
        if (!connected) return showToast(TOAST_INFO, 'Buy In', 'Please reconnect to the table to buy in')
        setBuyInLoader(true);
        if (openBuyInModal) {
            sendJoinProtocol(entryAmount, autoRebuyValue);
        }

        if (openRebuyInModal) {
            sendRebuyInProtocol(entryAmount, autoRebuyValue);
        }
    }

    useEffect(() => {
        if (openBuyInModal || openRebuyInModal) {
            setBuyInLoader(false);
        }
    }, [openBuyInModal, openRebuyInModal])

    useEffect(() => {
        if (timeLeft === 0 && openRebuyInModal) {
            dispatch(closeRebuyIn({ tempTableId }));
            sendDRebuyInProtocol();
        }
    }, [timeLeft])

    return (
        <Drawer dismissible={false} open={(openBuyInModal || openRebuyInModal) && connected}>
            <DrawerContent draggable={false} showCloseButton={false} onInteractOutside={handleOutsideClick} side="bottom" className={styles.container}>
                {/* Header with centered title and close button */}
                <div className={styles.header}>
                    <div className={styles.headerSpacer}></div>
                    <h2 className={styles.title}>{openRebuyInModal ? 'Re-Buy' : 'Buy In'}</h2>
                    <DrawerClose onClick={handleCloseMark} className={styles.closeButton}>
                        <IoCloseSharp className={styles.closeIcon} />
                    </DrawerClose>
                </div>

                <div className={styles.buyInContent}>
                    {/* Details section */}
                    <div className={styles.detailsSection}>
                        <div className={styles.detailRow}>
                            <div className={styles.leftContent}>
                                <span className={styles.detailLabel}>Blinds:</span>
                                <span className={styles.detailValue}>{smallBlind}/{bigBlind}</span>
                            </div>
                            <div className={styles.rightContent}>
                                <span className={styles.walletHeader}>Wallet Balance</span>
                            </div>
                        </div>

                        <div className={styles.detailRow}>
                            <div className={styles.leftContent}>
                                <span className={styles.detailLabel}>Max Buy In:</span>
                                <span className={styles.detailValue}>{RUPEE_SYMBOL + maxbuyInAmount}</span>
                            </div>
                            <div className={styles.rightContent}>
                                <div className={styles.walletBalance}>
                                    <FaWallet className={styles.walletIcon} />
                                    <span className={styles.balanceAmount}>{RUPEE_SYMBOL + totalBalance}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.buyInBottomRow}>
                        {/* Entry Amount */}
                        <div className={styles.detailRow}>
                            <div className={styles.leftContent}>
                                <span className={styles.entryAmountLabel}>Entry Amount :</span>
                            </div>
                            <div className={styles.entryAmountContainer}>
                                <span className={styles.entryAmountValue}>{RUPEE_SYMBOL + entryAmount}</span>
                            </div>
                        </div>

                        {/* Slider */}
                        {
                            !sliderDisabled &&
                            <div className={styles.sliderContainer}>

                                <Slider
                                    value={[entryAmount]}
                                    onValueChange={(value) => setEntryAmount(value[0])}
                                    min={sliderMin}
                                    max={sliderMax}
                                    disabled={sliderDisabled}
                                />
                                <div className={styles.sliderLabelContainer}>
                                    <span className={styles.sliderLabel}>Min. {RUPEE_SYMBOL + sliderMin}</span>
                                    <span className={styles.sliderLabel}>Max. {RUPEE_SYMBOL + sliderMax}</span>
                                </div>
                            </div>
                        }
                        {/* Anti-banking note */}
                        {showAntiBankNote && (
                            <div className={styles.antiBankNote}>
                                Note: As you left the table with {RUPEE_SYMBOL}{noteAmount} {antiBankingTime ? `in the last ${antiBankingTime} minutes` : ''}, you must buy in with at least that amount
                            </div>
                        )}
                    </div>

                    {/* Auto Re-Buy Checkbox */}
                    {/* <div className={styles.checkboxContainer}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                className={styles.checkbox}
                                defaultChecked={autoRebuyValue}
                                onChange={() => setAutoRebuyValue(!autoRebuyValue)}
                            />
                            <span className={styles.checkboxText}>Auto Re-Buy</span>
                        </label>
                    </div> */}

                    {/* Buy In Button with Timer */}
                    <div className={styles.buttonContainer}>
                        {buyInTimeInSeconds > 0 && (
                            <div className={styles.timerContainer}>
                                <span className={styles.timerText}>{formatTime(timeLeft)}</span>
                            </div>
                        )}
                        <button
                            className={`${styles.buyInButton} ${buyInTimeInSeconds > 0 ? styles.buyInButtonWithTimer : ''}`}
                            onClick={handleBuyInButton}
                        >
                            {buyInLoader ? 'Loading...' : openRebuyInModal ? 'Re-Buy' : 'Buy In'}
                        </button>
                    </div>
                </div>
            </DrawerContent>
        </Drawer>
    )
}

